import React, { useEffect, ComponentType, useMemo } from 'react';
import { isSyntheticTest } from '../../../utils/platformBasedInfo';
import { recordApplyEvent } from '../../../utils/recordEvent';
import { flowStepCompleteForPath, flowErrorForPath } from '../../../utils/gaFlow';
import { DataError } from '../../../api/DataError';
import { Alert2 as Alert, LoadingTento, useHasFullLoaderTimeElapsed } from 'shared-components';
import { AlertTitle2 as AlertTitle } from 'shared-components';
import { currencyToDecimal } from '../../../utils/currencyToDecimal';
import { addOwnersToPost } from './addOwnersToPost';
import { ContactFormattedResponse, EntireDealFormattedResponse } from '../../../types';
import {
  ApplyWizardStep,
  STEP_BCM_2_BUSINESS_MORE_DETAILS,
  STEP_BCM_BUSINESS_MORE_DETAILS,
  STEP_BCM_SUMMARY,
  STEP_BUSINESS_FURTHER_DETAILS,
  STEP_BUSINESS_MORE_DETAILS,
  STEP_BUSINESS_OWNERS,
  STEP_EQ_LEAD_NOT_READY,
  STEP_EQ_LEAD_OWNERS_INFORMATION,
  STEP_EQ_LEAD_PRODUCT_DETAILS,
  STEP_EQ_LEAD_SUMMARY,
  STEP_EQ_LEAD_YOUR_BUSINESS,
  STEP_EQ_LEAD_YOUR_INFORMATION,
  STEP_SUMMARY,
  STEP_TYPE_OF_FUNDING,
  STEP_WHEN_FUNDING_NEEDED,
} from '../../ApplyWizard/ApplyWizardSteps';
import { connect, useDispatch } from 'react-redux';
import { change, formValueSelector } from 'redux-form';
import { RootState } from '@store/reducers.js';
import { MAX_SANE_NUMBER_OF_OWNERS } from '@components/Apply/OwnersMoreDetailsAboutYou/getNumberOfOwners';
import { Dispatch } from 'redux';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';
import { createOrUpdateLead } from '../../../api/createOrUpdateLead';
import { useHistory } from 'react-router-dom';
import { BrandType } from '@store/actions';

const appSelector = formValueSelector('application');

const enrichOwnersWithLeadIds = (state: RootState) => (dispatch: Dispatch, contacts: ContactFormattedResponse[]) => {
  const maxOwners = MAX_SANE_NUMBER_OF_OWNERS;
  // Index-based matching with a no-double-assign guard — same fusion bug as
  // the standard flow (LandTech incident 2026-09-03, Trello 1954): matching
  // by email stamped two same-email owners with ONE lead-contact id. The
  // lead flow indexes owners from 0 (isNotAnOwner submitter block), so
  // owner_i maps to contacts[i].
  const assignedIds = new Set<string>();
  for (let i = 0; i <= maxOwners; i++) {
    const hasOwnerBlock =
      appSelector(state, `owner_${i}_lead_id`) ||
      appSelector(state, `owner_${i}_street`) ||
      appSelector(state, `owner_${i}_suite`) ||
      appSelector(state, `owner_${i}_city`) ||
      appSelector(state, `owner_${i}_state`) ||
      appSelector(state, `owner_${i}_zip`) ||
      appSelector(state, `owner_${i}_first`) ||
      appSelector(state, `owner_${i}_last`) ||
      appSelector(state, `owner_${i}_ssn`) ||
      appSelector(state, `owner_${i}_mobile`) ||
      appSelector(state, `owner_${i}_dob_day`) ||
      appSelector(state, `owner_${i}_dob_month`) ||
      appSelector(state, `owner_${i}_dob_year`) ||
      appSelector(state, `owner_${i}_title`) ||
      appSelector(state, `owner_${i}_ownership`) ||
      appSelector(state, `owner_${i}_email`);
    if (!hasOwnerBlock) continue;

    const existingId = appSelector(state, `owner_${i}_lead_id`);
    if (existingId) {
      assignedIds.add(existingId);
      continue;
    }

    let owner: ContactFormattedResponse | undefined = contacts[i];
    if (!owner || !owner.id || assignedIds.has(owner.id)) {
      owner = contacts.find(
        (c) => c.id && !assignedIds.has(c.id) && c.email === appSelector(state, `owner_${i}_email`)
      );
    }
    if (owner && owner.id && !assignedIds.has(owner.id)) {
      assignedIds.add(owner.id);
      dispatch(change('application', `owner_${i}_lead_id`, owner.id));
    }
  }
};

interface WithOnSubmitProps {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

type ExpectedProps = {
  partnerCustomFields?: any;
  lead_id: string;
  type_of_statements: 'email' | 'upload' | 'plaid';
  // referralPartnerId?: string;
  brand_params?: BrandType['data']['brand'];
  vendor_id?: string;
  vendor_name?: string;
  enrichOwners: ReturnType<typeof enrichOwnersWithLeadIds>;
  prefilled_loan_amount?: number;
  prefilled_equipment_description?: string;
  isNotAnOwner?: boolean;
};

export const withLeadOnSubmit = <P extends object>(WrappedComponent: ComponentType<P>, wizardStep: ApplyWizardStep) => {
  const HOC: React.FC<Omit<P, keyof WithOnSubmitProps> & ExpectedProps> = (props) => {
    const [{ data: leadUpdateResponse, loading: leadUpdateLoading, error: leadUpdateError }, updateOpportunity] =
      createOrUpdateLead();
    const dispatch = useDispatch();
    const history = useHistory();

    const WaitForSubmit = useMemo(
      () => () => {
        const hasFullAnimationCompleted = useHasFullLoaderTimeElapsed(5000);

        useEffect(() => {
          if (hasFullAnimationCompleted && leadUpdateResponse) {
            history.push('/eq/app/contact-soon');
          }
        }, [hasFullAnimationCompleted, leadUpdateResponse]);

        return (
          <div className="flex flex-1 items-center">
            <LoadingTento width={undefined} height={undefined} />
          </div>
        );
      },
      [leadUpdateResponse]
    );

    // GA4 funnel: the lead save succeeded, so the current EQ step is done
    // (the summary step navigates to /eq/decision from its own form).
    useEffect(() => {
      if (leadUpdateResponse) flowStepCompleteForPath(window.location.pathname);
    }, [leadUpdateResponse]);
    useEffect(() => {
      if (!leadUpdateError) return;
      const err = leadUpdateError as { response?: { data?: { message?: string } }; message?: string };
      flowErrorForPath(
        window.location.pathname,
        'api',
        err.response?.data?.message || err.message || 'lead_save_failed'
      );
    }, [leadUpdateError]);

    useEffect(() => {
      if (leadUpdateResponse && wizardStep !== STEP_EQ_LEAD_SUMMARY) {
        if (wizardStep === STEP_EQ_LEAD_NOT_READY) {
          (async () => {
            if (!process.env.STORYBOOK_TEST && !isSyntheticTest) {
              analytics.track('Lead Application Link Requested', {
                channel: 'sms',
                vendor: props.vendor_name,
                vendor_id: props.vendor_id,
                lead_link: leadUpdateResponse.password
                  ? `${window.location.origin}/lc/${leadUpdateResponse.password}`
                  : `${window.location.origin}/eq`,
              });
              recordApplyEvent('Lead Application Link Requested', {
                channel: 'sms',
                vendor: props.vendor_name,
                vendor_id: props.vendor_id,
                lead_link: leadUpdateResponse.password
                  ? `${window.location.origin}/lc/${leadUpdateResponse.password}`
                  : `${window.location.origin}/eq`,
              });
            }
          })();
        }
        if (wizardStep === STEP_EQ_LEAD_YOUR_INFORMATION) {
          dispatch(change('application', 'loan_type', ['EQ']));
          if (!props.prefilled_equipment_description) {
            history.push('/eq/product-details');
          } else {
            history.push('/eq/your-business');
          }
        }
        if (wizardStep === STEP_EQ_LEAD_PRODUCT_DETAILS) {
          history.push('/eq/your-business');
        }
        if (wizardStep === STEP_EQ_LEAD_YOUR_BUSINESS) {
          history.push('/eq/owners');
        }
        if (wizardStep === STEP_EQ_LEAD_OWNERS_INFORMATION) {
          history.push('/eq/summary');
        }
        // TODO: summary step is handled in WaitForSubmit
        // if (wizardStep === STEP_EQ_LEAD_SUMMARY) {
        //   history.push('/eq/todo');
        // }
      }
    }, [leadUpdateResponse]);

    useEffect(() => {
      if (process.env.STORYBOOK_TEST) return;
    }, []);

    if ((leadUpdateLoading || leadUpdateResponse) && wizardStep === STEP_EQ_LEAD_SUMMARY) {
      return <WaitForSubmit />;
    }

    const onSubmitFunction = async (form: any) => {
      let post: EntireDealFormattedResponse & {
        partnerId?: string;
      } = {};
      post.partnerId = props?.brand_params?.partner_id || '';

      if (form[`business_lead_id`]) {
        if (!post.business) {
          post.business = {};
        }
        post.business.id = form[`business_lead_id`];
      }

      if (wizardStep === STEP_EQ_LEAD_NOT_READY) {
        if (!post.business) {
          post.business = {};
        }
        if (!post.business.contacts) {
          post.business.contacts = [];
        }
        post.isNotReadyToApply = true;
        post.applyDomainUrl = window.location.origin;
        post.business.contacts = [
          {
            id: form['owner_1_lead_id'],
            phoneMobile: form['owner_1_mobile'],
            email: 'not.ready.to.apply.fake.email.this.is.not.a.real.user.email@gmail.com',
          },
        ];
        if (!post.loanRequest) {
          post.loanRequest = {
            id: props.lead_id,
            type: 'EQ',
            source: 'apply-lead-eq-flow',
            metadata: {
              loanTypes: ['EQ'],
            },
          };
        }
      }

      if (wizardStep === STEP_EQ_LEAD_YOUR_INFORMATION) {
        (async () => {
          if (!process.env.STORYBOOK_TEST && !isSyntheticTest) {
            analytics.track('Apply Lead EQ Form - Your Information', {
              ...form,
            });
            recordApplyEvent('Apply Lead EQ Form - Your Information', {
              ...form,
            });
          }
        })();
        if (!post.business) {
          post.business = {};
        }
        if (!post.business.contacts) {
          post.business.contacts = [];
        }
        if (props.isNotAnOwner) {
          post.business.contacts = [
            ...post.business.contacts,
            {
              id: form['owner_0_lead_id'],
              email: form['owner_0_email'],
              firstName: form['owner_0_first'],
              lastName: form['owner_0_last'],
              phoneMobile: form['owner_0_mobile'],
              isSubmitter: true,
            },
          ];
        } else {
          post.business.contacts = [
            ...post.business.contacts,
            {
              id: form['owner_1_lead_id'],
              email: form['owner_1_email'],
              firstName: form['owner_1_first'],
              lastName: form['owner_1_last'],
              phoneMobile: form['owner_1_mobile'],
            },
          ];
        }
        if (!post.loanRequest) {
          post.loanRequest = {
            id: props.lead_id,
            type: 'EQ',
            source: 'apply-lead-eq-flow',
            metadata: {
              loanTypes: ['EQ'],
            },
          };
        }
        if (form['loan_amount']) {
          post.loanRequest.amount = currencyToDecimal(form['loan_amount']);
        } else {
          post.loanRequest.amount = -1; // When EQ information is not available (not sourced in)
        }
        if (form[`equipment_description`]) {
          post.loanRequest.equipmentDesc = form.equipment_description;
        } else {
          post.loanRequest.equipmentDesc = 'Not specified / Not provided by Partner integration'; // When EQ information is not available (not sourced in)
        }
        if (form[`equipment_image`]) {
          post.loanRequest.equipmentImage = form.equipment_image;
        }
      }

      if (wizardStep === STEP_EQ_LEAD_PRODUCT_DETAILS) {
        (async () => {
          if (!process.env.STORYBOOK_TEST && !isSyntheticTest) {
            analytics.track('Apply Lead EQ Form - Product Information', {
              ...form,
            });
            recordApplyEvent('Apply Lead EQ Form - Product Information', {
              ...form,
            });
          }
        })();
        if (!post.loanRequest) {
          post.loanRequest = {
            id: props.lead_id,
            type: 'EQ',
            source: 'apply-lead-eq-flow',
            metadata: {
              loanTypes: ['EQ'],
            },
          };
        }
        if (form['loan_amount']) {
          post.loanRequest.amount = currencyToDecimal(form['loan_amount']);
        } else {
          post.loanRequest.amount = -1; // When EQ information is not available (not sourced in)
        }
        if (form[`equipment_description`]) {
          post.loanRequest.equipmentDesc = form.equipment_description;
        } else {
          post.loanRequest.equipmentDesc = 'Not specified / Not provided by Partner integration'; // When EQ information is not available (not sourced in)
        }
        if (form[`equipment_image`]) {
          post.loanRequest.equipmentImage = form.equipment_image;
        }
      }

      if (wizardStep === STEP_EQ_LEAD_YOUR_BUSINESS) {
        (async () => {
          if (!process.env.STORYBOOK_TEST && !isSyntheticTest) {
            analytics.track('Apply Lead EQ Form - Your Business', {
              ...form,
            });
            recordApplyEvent('Apply Lead EQ Form - Your Business', {
              ...form,
            });
          }
        })();
        if (!post.business) {
          post.business = {};
        }

        if (form['business_name']) {
          post.business.name = form['business_name'];
        }

        if (form['business_revenue']) {
          post.business.annualRevenue = currencyToDecimal(form['business_revenue']);
        }

        if (form['business_website']) {
          post.business.website = form['business_website'];
        }

        if (
          form.business_street ||
          form.business_suite ||
          form.business_city ||
          form.business_state ||
          form.business_zip
        ) {
          post.business.address = {
            address1: form.business_street || null,
            address2: form.business_suite || null,
            city: form.business_city || null,
            state: form.business_state || null,
            postalCode: form.business_zip || null,
          };
        }

        if (form[`business_years`] || form[`business_years`] === 0) {
          if (!post.business) {
            post.business = {};
          }
          if (!post.loanRequest) {
            post.loanRequest = {
              source: 'apply-lead-eq-flow',
            };
          }
          post.loanRequest.id = props.lead_id;
          post.business.businessYears = form[`business_years`];
        }

        if (form[`business_ein`]) {
          if (!post.business) {
            post.business = {};
          }
          post.business.taxIdNumber = form[`business_ein`];
          (async () => {
            if (!isSyntheticTest) {
              analytics.track('Apply Lead EQ Form - EIN', {
                ein: post?.business?.taxIdNumber,
              });
              recordApplyEvent('Apply Lead EQ Form - EIN', {
                ein: post?.business?.taxIdNumber,
              });
            }
          })();
        }
      }

      // if (wizardStep === STEP_BUSINESS_FURTHER_DETAILS) {
      //   if (form[`business_revenue`]) {
      //     if (!post.business) {
      //       post.business = {};
      //     }
      //     post.business.annualRevenue = currencyToDecimal(form[`business_revenue`]);
      //     (async () => {
      //       if (!isSyntheticTest) {
      //         analytics.track(
      //           applicationFlow === 'bcm' || applicationFlow === 'bcm-short'
      //             ? 'Apply BCM Form - Business Reported Revenue'
      //             : 'Apply Form - Business Reported Revenue',
      //           {
      //             revenue: post?.business?.annualRevenue,
      //           }
      //         );
      //       }
      //     })();
      //     (async () => {
      //       if (!isSyntheticTest) {
      //         analytics.track(
      //           applicationFlow === 'bcm' || applicationFlow === 'bcm-short'
      //             ? 'Apply BCM Form - Business Revenue'
      //             : 'Apply Form - Business Revenue',
      //           {
      //             revenue: post?.business?.annualRevenue,
      //           }
      //         );
      //       }
      //     })();
      //   }
      //   if (form[`business_ein`]) {
      //     if (!post.business) {
      //       post.business = {};
      //     }
      //     post.business.taxIdNumber = form[`business_ein`];
      //   }

      //   if (form[`business_revenue`] && dataLayerPush) {
      //     if (!isSyntheticTest) {
      //       dataLayerPush('user-reported-revenue', {
      //         account_id: post.business?.id,
      //         time_in_business: form[`business_revenue`],
      //       });
      //     }
      //   }
      // }

      if (wizardStep === STEP_EQ_LEAD_OWNERS_INFORMATION) {
        (async () => {
          if (!process.env.STORYBOOK_TEST && !isSyntheticTest) {
            analytics.track('Apply Lead EQ Form - Owners', {
              ...form,
            });
            recordApplyEvent('Apply Lead EQ Form - Owners', {
              ...form,
            });
          }
        })();
        if (!post.loanRequest) {
          post.loanRequest = {};
        }
        post.loanRequest.stage = 'AwaitingStatements';
        post = addOwnersToPost({ post, form, type: 'eqlead', isNotAnOwner: props.isNotAnOwner });
      }

      // if (wizardStep === STEP_BCM_SUMMARY || wizardStep === STEP_BCM_2_BUSINESS_MORE_DETAILS) {
      //   (async () => {
      //     if (!isSyntheticTest) {
      //       analytics.track('Apply BCM Form - Done', {
      //         ...form,
      //       });
      //     }
      //   })();
      //   if (!post.business) {
      //     post.business = {};
      //   }
      //   post.business.bcmStatus = 'approved';
      //   post.business.bcmSubscriptionType = 'freetrial';
      //   post.business.bcmSubscriptionSource = 'bcm-flow-onboarding';
      //   post.business.bcmSubscriptionValidUntil = 'indefinitely';
      //   if (form[`business_ein`]) {
      //     if (!post.business) {
      //       post.business = {};
      //     }
      //     post.business.taxIdNumber = form[`business_ein`];
      //   }
      //   if (form[`business_dba`]) {
      //     if (!post.business) {
      //       post.business = {};
      //     }
      //     post.business.doingBusinessAs = form[`business_dba`];
      //   }
      //   if (form[`business_name`]) {
      //     if (!post.business) {
      //       post.business = {};
      //     }
      //     post.business.name = form[`business_name`];
      //   }
      // }

      // if (wizardStep === STEP_SUMMARY) {
      //   (async () => {
      //     if (!isSyntheticTest) {
      //       analytics.track('Apply Form - Submit Application', {});
      //     }
      //   })();
      //   if (!post.business) {
      //     post.business = {};
      //   }
      //   post.business.bcmStatus = 'approved';
      //   post.business.bcmSubscriptionType = 'freetrial';
      //   post.business.bcmSubscriptionSource = 'apply-loan-submitted';
      //   post.business.bcmSubscriptionValidUntil = 'indefinitely';
      //   if (form[`loan_amount`]) {
      //     if (!post.loanRequest) {
      //       post.loanRequest = {};
      //     }
      //     post.loanRequest.amount = currencyToDecimal(form[`loan_amount`]);
      //   }
      //   if (form[`loan_terms`]) {
      //     if (!post.loanRequest) {
      //       post.loanRequest = {};
      //     }
      //     post.loanRequest.termUnit = 'month';
      //     post.loanRequest.termLength = form[`loan_terms`];
      //   }
      //   if (form[`business_revenue`]) {
      //     if (!post.business) {
      //       post.business = {};
      //     }
      //     post.business.annualRevenue = currencyToDecimal(form[`business_revenue`]);
      //   }
      //   if (form[`business_ein`]) {
      //     if (!post.business) {
      //       post.business = {};
      //     }
      //     post.business.taxIdNumber = form[`business_ein`];
      //   }
      //   if (form[`business_years`]) {
      //     if (!post.business) {
      //       post.business = {};
      //     }
      //     if (!post.loanRequest) {
      //       post.loanRequest = {};
      //     }
      //     post.loanRequest.id = props.lead_id;
      //     post.business.businessYears = form[`business_years`];
      //     (async () => {
      //       if (!isSyntheticTest) {
      //         analytics.track('Apply Form - Business Years', {
      //           years: post?.business?.businessYears,
      //         });
      //       }
      //     })();
      //   }
      //   if (form[`business_name`]) {
      //     if (!post.business) {
      //       post.business = {};
      //     }
      //     post.business.name = form[`business_name`];
      //   }
      //   if (!post.loanRequest) {
      //     post.loanRequest = {};
      //   }
      //   if (props.type_of_statements === 'plaid' || props.type_of_statements === 'upload') {
      //     post.loanRequest.stage = 'ApplySubmitted';
      //   } else {
      //     post.loanRequest.stage = 'AwaitingStatements';
      //   }
      // }

      const response = await updateOpportunity({ data: post });
      const leadUpdateResponse = response.data;

      if (leadUpdateResponse && leadUpdateResponse.accountId) {
        dispatch(change('application', 'business_lead_id', leadUpdateResponse.accountId));
      }
      if (leadUpdateResponse && leadUpdateResponse.opportunityId) {
        dispatch(change('application', 'lead_id', leadUpdateResponse.opportunityId));
      }
      if (leadUpdateResponse?.contactIds?.length > 0) {
        props.enrichOwners(dispatch, leadUpdateResponse.contactIds);
      }
    };

    // Keep `saving` truthy from the moment the request starts until the
    // step unmounts via history.push(). Without this, the
    // LoanSubmittingOverlay flashes off for a frame between API success
    // and the route change. Mirrors withOnSubmit.tsx fix.
    const isSubmittingOrPendingNav = leadUpdateLoading || !!leadUpdateResponse;

    return (
      <div className="flex flex-1 flex-col gap-4">
        <WrappedComponent
          {...(props as P)}
          onSubmit={onSubmitFunction}
          saving={isSubmittingOrPendingNav}
          dealUpdateResponse={leadUpdateResponse}
        />
        {leadUpdateError && (
          <Alert severity="error">
            <AlertTitle>Failed to save</AlertTitle>
            <DataError msg="Unfortunately, we couldn't save your update." error={leadUpdateError} />
          </Alert>
        )}
      </div>
    );
  };

  HOC.displayName = `withLeadOnSubmit(${getDisplayName(WrappedComponent)})`;
  const prefilledSelector = formValueSelector('prefilled');

  const mapStateToProps = (state: RootState) => {
    return {
      enrichOwners: enrichOwnersWithLeadIds(state),
      partnerCustomFields: state.partnerCustomFields,
      lead_id: appSelector(state, 'lead_id'),
      type_of_statements: appSelector(state, 'type_of_statements'),
      // referralPartnerId: appSelector(state, 'referralPartnerId'),
      brand_params: state.brand?.data?.brand,
      vendor_id: state.brand?.data?.vendor_id,
      vendor_name: state.brand?.data?.vendor_name,

      isNotAnOwner: appSelector(state, 'isNotAnOwner'),

      loan_amount: appSelector(state, 'loan_amount'),
      equipment_description: appSelector(state, 'equipment_description'),
      prefilled_loan_amount: prefilledSelector(state, 'loan_amount'),
      prefilled_equipment_description: prefilledSelector(state, 'equipment_description'),
    };
  };

  return connect(mapStateToProps)(HOC as any);
};

const getDisplayName = (WrappedComponent: ComponentType<any>): string => {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
};

export default withLeadOnSubmit;
